import type { TrackPoint } from "@/types";

export interface ProjectionBounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

export function convertGPSToSVG(
  points: TrackPoint[],
  bounds: ProjectionBounds,
  svgWidth: number,
  svgHeight: number,
  padding: number = 20,
): { x: number; y: number }[] {
  const latRange = bounds.maxLat - bounds.minLat;
  const lngRange = bounds.maxLng - bounds.minLng;

  // Use the larger range to maintain aspect ratio
  const range = Math.max(latRange, lngRange);

  // Degenerate track (all points identical): range 0 would divide to
  // Infinity (NaN coords); map everything to the center instead.
  const scale =
    range === 0
      ? 0
      : Math.min(
          (svgWidth - 2 * padding) / range,
          (svgHeight - 2 * padding) / range,
        );

  // Center the track in the SVG
  const centerX = svgWidth / 2;
  const centerY = svgHeight / 2;
  const trackCenterLat = (bounds.minLat + bounds.maxLat) / 2;
  const trackCenterLng = (bounds.minLng + bounds.maxLng) / 2;

  return points.map((point) => ({
    x: centerX + (point.lng - trackCenterLng) * scale,
    y: centerY - (point.lat - trackCenterLat) * scale, // Flip Y axis for SVG
  }));
}

export function calculateBounds(points: TrackPoint[]): ProjectionBounds {
  return points.reduce(
    (bounds, point) => ({
      minLat: Math.min(bounds.minLat, point.lat),
      maxLat: Math.max(bounds.maxLat, point.lat),
      minLng: Math.min(bounds.minLng, point.lng),
      maxLng: Math.max(bounds.maxLng, point.lng),
    }),
    {
      minLat: points[0]?.lat || 0,
      maxLat: points[0]?.lat || 0,
      minLng: points[0]?.lng || 0,
      maxLng: points[0]?.lng || 0,
    },
  );
}

export function createPathFromPoints(points: { x: number; y: number }[]): string {
  if (points.length === 0) return "";

  const [first, ...rest] = points;
  let path = `M ${first.x},${first.y}`;

  for (const point of rest) {
    path += ` L ${point.x},${point.y}`;
  }

  return path;
}
